import Breadcrumbs from "@/components/Breadcrumbs";
import { User } from "@/services/admin-services/models";
import { UsersService } from "@/services/admin-services/UsersService";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { nanoid } from "nanoid";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Users",
        to: "",
    },
    {
        title: "Manage",
        to: "",
    },
];

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [users, setUsers] = useState<User[]>([]);
    const [addUser, setAddUser] = useState<boolean>(false);

    const theme = useTheme();
    const navigate = useNavigate();

    const usersService = useMemo(() => new UsersService(), []);

    useEffect(() => {
        usersService.getUsers().then((users) => {
            setUsers(users);
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderStatus = (blocked: boolean) => {
        let statusColor = theme.palette.success;
        if (blocked) statusColor = theme.palette.error;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                {blocked ? "Blocked" : "Active"}
            </Typography>
        );
    };

    const onSubmit = () => {
        setAddUser(false);

        usersService.getUsers().then((users) => {
            setUsers(users);
            setIsLoading(false);
        });
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex" }}>
                    <Button
                        variant="contained"
                        startIcon={<PlusIcon className="w-5 h-5" />}
                        sx={{ ml: "auto" }}
                        onClick={() => setAddUser(true)}
                    >
                        Add User
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Name
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Email
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Last Login
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Status
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((item, i) => (
                                        <TableRow key={i} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                            <TableCell component="th" scope="row">
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    {item.fullName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell scope="row">
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    {item.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell scope="row">
                                                <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                    {item.lastLogin.toString() !== "0001-01-01T00:00:00"
                                                        ? parseJSON(item.lastLogin).toLocaleString("nl-be")
                                                        : "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell scope="row">{renderStatus(item.blocked)}</TableCell>
                                            <TableCell sx={{ p: 0 }}>
                                                <Box sx={{ display: "flex" }}>
                                                    <Button
                                                        color={"primary"}
                                                        sx={{ ml: "auto", mr: 1 }}
                                                        onClick={() => navigate(item.userId)}
                                                    >
                                                        Edit
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {addUser && <CreateDialog open={addUser} onClose={() => setAddUser(false)} onSubmit={onSubmit} />}
        </Box>
    );
};

export default Overview;

interface CreateDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
}
export const CreateDialog = ({ open, onClose, onSubmit }: CreateDialogProps) => {
    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const usersService = new UsersService();

    const onFormSubmit = (event: FormEvent) => {
        event.preventDefault();

        if (!name || !email) return;

        setIsSaving(true);

        usersService
            .createUser({
                name,
                email,
                password: nanoid(),
            })
            .then(() => {
                setIsSaving(false);
                onSubmit();
            });
    };

    const onCancel = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"} fullScreen={fullScreen}>
            <form onSubmit={(event) => onFormSubmit(event)}>
                <DialogTitle>Add new user</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        pointerEvents: isSaving ? "none" : "auto",
                    }}
                >
                    <TextField
                        autoFocus
                        fullWidth
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        label={`Name`}
                        type="text"
                        variant="standard"
                        sx={{ mb: 3 }}
                        disabled={isSaving}
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        label={`Email`}
                        type="text"
                        variant="standard"
                        sx={{ mb: 3 }}
                        disabled={isSaving}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button variant="contained" type="submit" disabled={isSaving}>
                        Add User
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
